import { html, type TemplateResult, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ExtBaseElement } from '../../core/ExtBaseElement';
import './ext-table.css';

export type ExtTableAlign = 'left' | 'center' | 'right';
export type ExtTableRender = 'text' | 'chip' | 'money' | 'date' | 'icon';
export type ExtChipKindLite = 'neutral' | 'success' | 'danger' | 'warning' | 'info';

export interface ExtTableColumn {
  key: string;
  label: string;
  sortable?: boolean;
  width?: string;
  align?: ExtTableAlign;
  render?: ExtTableRender;
  /** Mapping de valor -> kind para render: chip. Si no se pasa, default neutral. */
  kindMap?: Record<string, ExtChipKindLite>;
}

export interface ExtTableSort {
  key: string;
  direction: 'asc' | 'desc';
}

export interface ExtTablePagination {
  offset: number;
  limit: number;
  total: number;
}

type Row = Record<string, unknown>;

/**
 * <ext-table .columns=${[...]} .rows=${[...]} .sort=${null} selectable>
 *
 * Tabla con:
 *  - columnas configurables (sortable, width, align, render type).
 *  - renderers built-in: text, chip, money, date, icon.
 *  - loading / empty states.
 *  - paginacion opcional (controlada por parent).
 *  - seleccion por checkbox (controlada por parent).
 *
 * Eventos:
 *  - ext:sort       - { key, direction }
 *  - ext:row-click  - { row, index }
 *  - ext:page       - { offset, limit }
 *  - ext:select     - { selected: string[] }
 */
@customElement('ext-table')
export class ExtTable extends ExtBaseElement {
  @property({ attribute: false }) columns: ExtTableColumn[] = [];
  @property({ attribute: false }) rows: Row[] = [];
  @property({ attribute: false }) sort: ExtTableSort | null = null;
  @property({ type: Boolean, reflect: true }) loading = false;
  @property({ type: String, attribute: 'empty-label' }) emptyLabel = 'Sin datos';
  @property({ type: String, attribute: 'row-key' }) rowKey = 'id';
  @property({ type: Boolean, reflect: true }) selectable = false;
  @property({ attribute: false }) selected: string[] = [];
  @property({ type: Boolean, reflect: true }) striped = false;
  @property({ type: Boolean, reflect: true }) compact = false;
  @property({ attribute: false }) pagination: ExtTablePagination | null = null;

  private _moneyFmt = new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  private _onHeaderClick(col: ExtTableColumn): void {
    if (!col.sortable) return;
    const curr = this.sort;
    let direction: 'asc' | 'desc' = 'asc';
    if (curr && curr.key === col.key && curr.direction === 'asc') {
      direction = 'desc';
    }
    this._emit('sort', { key: col.key, direction });
  }

  private _onRowClick(row: Row, index: number, ev: MouseEvent): void {
    const target = ev.target as HTMLElement;
    if (target.closest('.ext-table__checkbox')) return;
    this._emit('row-click', { row, index });
  }

  private _onSelectAll = (ev: Event): void => {
    const checked = (ev.target as HTMLInputElement).checked;
    const newSel = checked
      ? this.rows.map((r) => String(r[this.rowKey] ?? ''))
      : [];
    this._emit('select', { selected: newSel });
  };

  private _onSelectRow = (row: Row, ev: Event): void => {
    const checked = (ev.target as HTMLInputElement).checked;
    const key = String(row[this.rowKey] ?? '');
    const curr = new Set(this.selected);
    if (checked) curr.add(key);
    else curr.delete(key);
    this._emit('select', { selected: Array.from(curr) });
  };

  private _formatMoney(v: unknown): string {
    if (v === null || v === undefined || v === '') return '';
    const n = typeof v === 'number' ? v : Number(v);
    if (Number.isNaN(n)) return String(v);
    return this._moneyFmt.format(n);
  }

  private _formatDate(v: unknown): string {
    if (!v) return '';
    const s = String(v);
    // Intenta parsear YYYY-MM-DD HH:mm[:ss] o ISO.
    const d = new Date(s.replace(' ', 'T'));
    if (Number.isNaN(d.getTime())) return s;
    const pad = (n: number): string => String(n).padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  private _renderCell(col: ExtTableColumn, row: Row): TemplateResult | string {
    const raw = row[col.key];
    const render: ExtTableRender = col.render ?? 'text';

    if (render === 'chip') {
      const str = raw === null || raw === undefined ? '' : String(raw);
      const kind: ExtChipKindLite = col.kindMap?.[str] ?? 'neutral';
      return html`<ext-chip kind=${kind} size="sm">${str}</ext-chip>`;
    }
    if (render === 'money') {
      return this._formatMoney(raw);
    }
    if (render === 'date') {
      return this._formatDate(raw);
    }
    if (render === 'icon') {
      const name = raw === null || raw === undefined ? '' : String(raw);
      if (!name) return '';
      return html`<ext-icon name=${name} size="sm"></ext-icon>`;
    }
    return raw === null || raw === undefined ? '' : String(raw);
  }

  private _renderHeader(): TemplateResult {
    const selSet = new Set(this.selected);
    const rowKeys = this.rows.map((r) => String(r[this.rowKey] ?? ''));
    const allChecked =
      this.rows.length > 0 && rowKeys.every((k) => selSet.has(k));
    const someChecked = !allChecked && rowKeys.some((k) => selSet.has(k));

    return html`
      <thead>
        <tr>
          ${this.selectable
            ? html`<th class="ext-table__select-cell">
                <input
                  type="checkbox"
                  class="ext-table__checkbox"
                  aria-label="Seleccionar todo"
                  .checked=${allChecked}
                  .indeterminate=${someChecked}
                  @change=${this._onSelectAll}
                />
              </th>`
            : nothing}
          ${this.columns.map((col) => {
            const isSorted = this.sort && this.sort.key === col.key;
            const direction = isSorted ? this.sort!.direction : null;
            const cls = [
              'ext-table__th',
              col.sortable ? 'is-sortable' : '',
              isSorted ? 'is-sorted' : '',
              col.align ? `align-${col.align}` : '',
            ]
              .filter(Boolean)
              .join(' ');
            const style = col.width ? `width: ${col.width};` : '';
            return html`<th
              class=${cls}
              style=${style}
              @click=${() => this._onHeaderClick(col)}
              aria-sort=${isSorted ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
            >
              <span class="ext-table__th-label">${col.label}</span>
              ${col.sortable
                ? html`<span class="ext-table__chevron" aria-hidden="true">
                    ${direction === 'asc' ? '\u25B2' : direction === 'desc' ? '\u25BC' : '\u25C7'}
                  </span>`
                : nothing}
            </th>`;
          })}
        </tr>
      </thead>
    `;
  }

  private _renderBody(): TemplateResult {
    if (this.rows.length === 0 && !this.loading) {
      const colspan = this.columns.length + (this.selectable ? 1 : 0);
      return html`
        <tbody>
          <tr>
            <td class="ext-table__empty" colspan=${colspan}>
              <ext-icon name="inbox" size="lg" aria-hidden="true"></ext-icon>
              <div class="ext-table__empty-label">${this.emptyLabel}</div>
            </td>
          </tr>
        </tbody>
      `;
    }

    const selSet = new Set(this.selected);
    return html`
      <tbody>
        ${this.rows.map((row, idx) => {
          const key = String(row[this.rowKey] ?? idx);
          const isSel = selSet.has(key);
          return html`<tr
            class=${isSel ? 'is-selected' : ''}
            @click=${(ev: MouseEvent) => this._onRowClick(row, idx, ev)}
          >
            ${this.selectable
              ? html`<td class="ext-table__select-cell">
                  <input
                    type="checkbox"
                    class="ext-table__checkbox"
                    aria-label="Seleccionar fila"
                    .checked=${isSel}
                    @change=${(ev: Event) => this._onSelectRow(row, ev)}
                  />
                </td>`
              : nothing}
            ${this.columns.map((col) => {
              const cls = ['ext-table__td', col.align ? `align-${col.align}` : '']
                .filter(Boolean)
                .join(' ');
              return html`<td class=${cls}>${this._renderCell(col, row)}</td>`;
            })}
          </tr>`;
        })}
      </tbody>
    `;
  }

  private _renderPagination(): TemplateResult | typeof nothing {
    if (!this.pagination) return nothing;
    const { offset, limit, total } = this.pagination;
    const from = total === 0 ? 0 : offset + 1;
    const to = Math.min(offset + limit, total);
    const canPrev = offset > 0;
    const canNext = offset + limit < total;

    const onPrev = (): void => {
      if (!canPrev) return;
      const newOffset = Math.max(0, offset - limit);
      this._emit('page', { offset: newOffset, limit });
    };
    const onNext = (): void => {
      if (!canNext) return;
      this._emit('page', { offset: offset + limit, limit });
    };

    return html`
      <div class="ext-table__pagination">
        <span class="ext-table__pagination-info">
          Mostrando ${from}-${to} de ${total}
        </span>
        <div class="ext-table__pagination-actions">
          <button
            type="button"
            class="ext-table__pagination-btn"
            ?disabled=${!canPrev}
            @click=${onPrev}
            aria-label="Pagina anterior"
          >
            &#x25C0;
          </button>
          <button
            type="button"
            class="ext-table__pagination-btn"
            ?disabled=${!canNext}
            @click=${onNext}
            aria-label="Pagina siguiente"
          >
            &#x25B6;
          </button>
        </div>
      </div>
    `;
  }

  render(): TemplateResult {
    return html`
      <div class="ext-table__wrapper">
        <table class="ext-table__table">
          ${this._renderHeader()} ${this._renderBody()}
        </table>
        ${this.loading
          ? html`<div class="ext-table__loading" aria-live="polite">
              <ext-spinner size="md" label="Cargando..."></ext-spinner>
            </div>`
          : nothing}
      </div>
      ${this._renderPagination()}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ext-table': ExtTable;
  }
}
